<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { NButton, NEmpty, NInput, NSpin } from 'naive-ui'
import {
  useMemoryEntriesStore,
  GLOBAL_PROJECT,
  type MemoryEntry,
  type MemoryScopeGroup,
} from '../stores/memory_entries'
import { useDocumentTitle } from '../composables/useDocumentTitle'
import WikiPageCard from './WikiPageCard.vue'
import MemoryEntryDrawer from './MemoryEntryDrawer.vue'

// Memory browse pane. Renders inside
// HomeView's shell so the catalog sidebar stays visible. Fetches every
// entry (`project=all`) and groups them by scope (Global + one section
// per project, each with a count). Entries use the shared `WikiPageCard`.
//
// Also adds browse-at-scale controls: a client-side filter + sort
// over the already-fetched entries, per-scope collapse for large
// sections, and a manual refresh so an external (CLI/MCP) write can be
// pulled in without a full reload.
//
// Admin-only: the memory API is admin-gated and the `/memory` route
// redirects non-admins before this mounts. It trusts that gate.

const TYPE_LABELS: Record<string, string> = {
  feedback: 'Feedback',
  reference: 'Reference',
  project: 'Project note',
}
function typeLabel(type: string): string {
  return TYPE_LABELS[type] ?? type
}

const store = useMemoryEntriesStore()
const router = useRouter()

useDocumentTitle('Memory')

onMounted(() => {
  if (!store.loaded) void store.fetch()
})

// ── Browse controls ─────────────────────────────────────────────────────
const filterQuery = ref('')
const sortKey = ref<'name' | 'updated'>('updated')
// Project keys the user expanded past the collapse limit.
const expandedScopes = ref<Set<string>>(new Set())

// Max cards shown per scope before collapsing — three rows of the
// 3-column xl grid. Larger scopes get a "Show all" disclosure.
const COLLAPSED_LIMIT = 9

interface DisplayGroup extends MemoryScopeGroup {
  hiddenCount: number
}

function matches(entry: MemoryEntry, q: string): boolean {
  return `${entry.name} ${entry.title} ${entry.description}`.toLowerCase().includes(q)
}

// Filter + sort + collapse applied over the store's scope groups. Empty
// groups (filtered to nothing) drop out. `count` reflects the filtered
// total; `hiddenCount` drives the per-scope "Show all" button.
const displayGroups = computed<DisplayGroup[]>(() => {
  const q = filterQuery.value.trim().toLowerCase()
  return store.groups
    .map((group): DisplayGroup => {
      let entries = q ? group.entries.filter((e) => matches(e, q)) : group.entries
      if (sortKey.value === 'updated') {
        entries = entries.toSorted((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''))
      }
      const total = entries.length
      const expanded = expandedScopes.value.has(group.project) || total <= COLLAPSED_LIMIT
      const visible = expanded ? entries : entries.slice(0, COLLAPSED_LIMIT)
      return { ...group, entries: visible, count: total, hiddenCount: total - visible.length }
    })
    .filter((group) => group.count > 0)
})

const noFilterMatches = computed(
  () => store.totalCount > 0 && filterQuery.value.trim() !== '' && displayGroups.value.length === 0,
)

function toggleScope(project: string): void {
  const next = new Set(expandedScopes.value)
  if (next.has(project)) next.delete(project)
  else next.add(project)
  expandedScopes.value = next
}

// ── New-entry drawer ────────────────────────────────────────────────────
const drawerShow = ref(false)

// Scopes offered in the drawer's create select: 'global' is
// always available even when it currently holds no entries.
const scopeSlugs = computed(() => {
  const slugs = new Set<string>([GLOBAL_PROJECT])
  for (const group of store.groups) slugs.add(group.project)
  return [...slugs]
})

function openView(entry: MemoryEntry): void {
  void router.push(`/memory/${encodeURIComponent(entry.project)}/${encodeURIComponent(entry.name)}`)
}
function openCreate(): void {
  drawerShow.value = true
}
</script>

<template>
  <section class="pt-2 pb-8 flex flex-col gap-6" data-testid="memory-view">
    <header class="flex items-start justify-between gap-3">
      <div class="flex flex-col gap-1">
        <h1 class="text-2xl font-semibold text-white m-0">Memory</h1>
        <p class="text-sm text-white/60 m-0">
          Owner-curated memory entries, grouped by scope. Cross-cutting notes live under
          <code class="text-white/50">Global</code>; the rest are scoped to a specific project.
        </p>
      </div>
      <div v-if="store.loaded" class="flex items-center gap-2 shrink-0">
        <NButton size="small" quaternary data-testid="memory-refresh" :loading="store.loading" @click="store.fetch()"
          >Refresh</NButton
        >
        <NButton size="small" type="primary" data-testid="memory-new-entry" @click="openCreate">New entry</NButton>
      </div>
    </header>

    <!--
      Branch precedence: first-load → error → empty → data. `loading &&
      !loaded` is the useAsyncResource "in-flight and nothing to show
      yet" condition: the full-page spinner shows only on the
      first fetch. A later refresh sets `loading` while `loaded` stays
      true, so it doesn't blank the list — the header Retry button shows
      its own spinner instead. A failed fetch clears `loading` and sets
      `loadError`, so it falls through to the error branch below.
    -->
    <div v-if="store.loading && !store.loaded" class="flex items-center justify-center py-12">
      <NSpin />
    </div>

    <div v-else-if="store.loadError" class="text-red-300 text-sm" data-testid="memory-load-error">
      Failed to load: {{ store.loadError }}
    </div>

    <NEmpty
      v-else-if="store.totalCount === 0"
      description="No memory entries yet. The brain writes them from Claude Code sessions; you can also add them via the CLI or API."
      class="my-12"
    />

    <template v-else>
      <!-- Scope-count summary so the global-vs-project split is the
           first thing the eye lands on (the issue's core ask). -->
      <p data-testid="memory-summary" class="text-sm text-white/70 m-0 tabular-nums">
        <span class="text-white font-medium">{{ store.globalCount }}</span> global<!--
        --><template v-if="store.projectScopeCount > 0">
          ·
          <span class="text-white font-medium">{{ store.projectCount }}</span>
          project-specific across
          <span class="text-white font-medium">{{ store.projectScopeCount }}</span>
          project{{ store.projectScopeCount === 1 ? '' : 's' }}
        </template>
      </p>

      <!-- Filter + sort over the already-fetched entries. -->
      <div class="flex flex-wrap items-center gap-3">
        <NInput
          v-model:value="filterQuery"
          size="small"
          clearable
          placeholder="Filter entries…"
          data-testid="memory-filter"
          class="max-w-xs"
        />
        <div class="flex items-center gap-1 text-xs text-white/40">
          <span>Sort</span>
          <button
            type="button"
            data-testid="memory-sort-name"
            class="px-2 py-1 rounded transition-colors"
            :class="sortKey === 'name' ? 'bg-white/[0.08] text-white' : 'hover:text-white'"
            @click="sortKey = 'name'"
          >
            Name
          </button>
          <button
            type="button"
            data-testid="memory-sort-updated"
            class="px-2 py-1 rounded transition-colors"
            :class="sortKey === 'updated' ? 'bg-white/[0.08] text-white' : 'hover:text-white'"
            @click="sortKey = 'updated'"
          >
            Recent
          </button>
        </div>
      </div>

      <p v-if="noFilterMatches" data-testid="memory-no-matches" class="text-sm text-white/50 m-0">
        No entries match “{{ filterQuery.trim() }}”.
        <button type="button" class="text-brand-primary hover:underline" @click="filterQuery = ''">Clear filter</button>
      </p>

      <section
        v-for="group in displayGroups"
        :key="group.project"
        data-testid="memory-scope-group"
        class="flex flex-col gap-2"
      >
        <div class="flex items-baseline justify-between gap-3">
          <h2 class="text-sm font-medium text-white/80 m-0 flex items-center gap-2">
            <span
              class="inline-block w-2 h-2 rounded-sm"
              :class="group.isGlobal ? 'bg-purple-500' : 'bg-blue-500'"
              aria-hidden="true"
            />
            {{ group.label }}
          </h2>
          <span class="text-xs text-white/40 tabular-nums">
            {{ group.count }} entr{{ group.count === 1 ? 'y' : 'ies' }}
          </span>
        </div>

        <ul
          :id="`memory-scope-${group.project}`"
          class="list-none p-0 m-0 grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3"
        >
          <WikiPageCard
            v-for="entry in group.entries"
            :key="entry.path"
            :path="entry.path"
            :title="entry.title || entry.name"
            :created-at="entry.created_at"
            :updated-at="entry.updated_at"
            :tag="typeLabel(entry.type)"
            :description="entry.description"
            hide-path
            selectable
            @select="openView(entry)"
          />
        </ul>

        <button
          v-if="group.hiddenCount > 0"
          type="button"
          data-testid="memory-show-all"
          class="self-start text-xs text-brand-primary hover:underline"
          :aria-expanded="expandedScopes.has(group.project)"
          :aria-controls="`memory-scope-${group.project}`"
          @click="toggleScope(group.project)"
        >
          Show all {{ group.count }}
        </button>
        <button
          v-else-if="expandedScopes.has(group.project) && group.count > COLLAPSED_LIMIT"
          type="button"
          data-testid="memory-show-less"
          class="self-start text-xs text-white/40 hover:text-white"
          :aria-expanded="expandedScopes.has(group.project)"
          :aria-controls="`memory-scope-${group.project}`"
          @click="toggleScope(group.project)"
        >
          Show less
        </button>
      </section>
    </template>

    <MemoryEntryDrawer v-model:show="drawerShow" :projects="scopeSlugs" />
  </section>
</template>
